import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import LogoIcon from '../ui/LogoIcon';

const Footer: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const [email,      setEmail]      = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast('Please enter a valid email address', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.subscribeToNewsletter(email);
      toast(res.message, 'success', 5000);
      setEmail('');
    } catch (err: any) {
      toast(err?.response?.data?.detail || 'Failed to subscribe. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const col = 'flex flex-col gap-2';
  const lnk = 'text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200';

  return (
    <footer className="bg-white border-t border-slate-200 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* Top row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex flex-col gap-1 mb-4">
              <div className="flex items-center gap-2">
                <LogoIcon className="h-7 w-7 text-primary-600" />
                <span className="text-lg font-extrabold tracking-tight gradient-text">Linzara</span>
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap font-medium">Visa-aware hiring, made clear and simple</span>
            </Link>
            <p className="text-sm text-slate-500 leading-relaxed mb-4">
              Built on trust, clarity, and opportunity — helping visa holders access clearer employment pathways, while helping employers and partners connect with skilled, diverse, work-ready talent.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              About us <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Product */}
          <div className={col}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Product</p>
            {isAuthenticated && user?.role === 'employer' && (
              <Link to="/employer/post-job" className={lnk}>Post Jobs</Link>
            )}
            {isAuthenticated && user?.role === 'student' ? (
              <Link to="/saved" className={lnk}>Saved Jobs</Link>
            ) : (
              <span className="text-sm text-slate-300 cursor-not-allowed">Saved Jobs</span>
            )}
            {isAuthenticated ? (
              <Link to="/profile" className={lnk}>Profile</Link>
            ) : (
              <Link to="/auth?tab=login" className={lnk}>Sign In</Link>
            )}
            <Link to="/pricing" className={lnk}>Pricing</Link>
            <Link to="/request-demo" className={lnk}>Request Demo</Link>
          </div>

          {/* Resources */}
          <div className={col}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Resources</p>
            <Link to="/about"              className={lnk}>About Us</Link>
            <Link to="/events"             className={lnk}>Events</Link>
            <Link to="/blog"               className={lnk}>Blog</Link>
            <Link to="/employer-visa-guide"className={lnk}>Visa Guide</Link>
            <Link to="/how-it-works"       className={lnk}>How It Works</Link>
            <Link to="/request-demo"       className={lnk}>Partners</Link>
            <Link to="/contact" className={lnk}>Contact</Link>
          </div>

          {/* Newsletter */}
          <div className={col}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Stay Updated</p>
            <p className="text-sm text-slate-500 mb-3">
              Get the latest visa-friendly roles and platform news.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={submitting}
                  required
                  className="input-field pl-9 text-sm py-2.5"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary py-2.5 text-sm"
              >
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom row */}
        <div className="pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <p>© {new Date().getFullYear()} Linzara. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-slate-700 transition-colors">Privacy</Link>
            <Link to="/terms"   className="hover:text-slate-700 transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-slate-700 transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
